import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { todayIn } from '@/shared/lib/calendar';
import { Button, HelpRow } from '@/shared/ui';

import { CreateEventDialog, type EventPrefill } from './CreateEventDialog';
import { draftFromDays } from './model';
import { prefillOf } from './nl/prefill';
import { parseSentence } from './nl/sentence';
import { MAX_INPUT_LENGTH, type ParsedSentence } from './nl/types';
import styles from './TextEventDialog.module.css';

type DialogProps = Omit<
  React.ComponentProps<typeof CreateEventDialog>,
  'draft' | 'heading' | 'intro' | 'prefill'
>;

/** What was read, as plain facts - the form below is where each of them is changed. */
function Interpretation({ parsed }: { parsed: ParsedSentence }) {
  const { t } = useTranslation();
  const none = t('calendar.text.interp.none');
  const when = [parsed.startDate, parsed.startTime].filter(Boolean).join(' ');
  const end = [parsed.endDate, parsed.endTime].filter(Boolean).join(' ');
  const rows: [string, string][] = [
    [t('calendar.text.interp.title'), parsed.title || none],
    [t('calendar.text.interp.kind'), t(`calendar.text.interp.kind_${parsed.kind}`)],
    [
      t(parsed.kind === 'deadline' ? 'calendar.text.interp.due' : 'calendar.text.interp.start'),
      when || none,
    ],
    ...(parsed.kind === 'event'
      ? [[t('calendar.text.interp.end'), end || none] as [string, string]]
      : []),
    ...(parsed.board
      ? [[t('calendar.text.interp.board'), parsed.board.name] as [string, string]]
      : []),
  ];
  return (
    <dl className={styles.interp} data-testid="text-interpretation" data-kind={parsed.kind}>
      {rows.map(([label, value]) => (
        <div key={label} className={styles.interpRow}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Quick creation from a sentence ("Работа завтра с 18:45 до 20:45"). The sentence is read by the
 * local grammar (`nl/parse.ts`, no AI) and the result fills the ordinary "new event" form, which
 * stays the one place where the event is checked, completed and CREATED - by an explicit Save.
 * Whatever could not be read is listed and left empty for the person; the text is never discarded.
 */
export function TextEventDialog({ timezone, boards, ...rest }: DialogProps) {
  const { t } = useTranslation();
  const id = useId();
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<ParsedSentence | null>(null);
  const [prefill, setPrefill] = useState<EventPrefill | null>(null);
  const input = useRef<HTMLInputElement>(null);
  // The dialog starts on its close button and the form on the title: the sentence comes first here.
  useEffect(() => {
    const timer = window.setTimeout(() => input.current?.focus(), 50);
    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  const recognize = () => {
    if (!text.trim()) return;
    const result = parseSentence(text, {
      today: todayIn(timezone),
      boards: boards.map((board) => ({ id: board.id, name: board.name })),
    });
    setParsed(result);
    setPrefill((current) => prefillOf((current?.id ?? 0) + 1, result));
  };

  const intro = (
    <section className={styles.intro} aria-label={t('calendar.text.title')}>
      <HelpRow topic={t('calendar.text.title')} topicId="quick-create">
        <label htmlFor={`${id}-text`} className={styles.label}>
          {t('calendar.text.label')}
        </label>
      </HelpRow>
      <div className={styles.inputRow}>
        <input
          id={`${id}-text`}
          ref={input}
          type="text"
          className={styles.input}
          value={text}
          maxLength={MAX_INPUT_LENGTH}
          autoComplete="off"
          placeholder={t('calendar.text.placeholder')}
          onChange={(event) => {
            setText(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault(); // recognise, never submit a form
              recognize();
            }
          }}
        />
        <Button variant="secondary" disabled={!text.trim()} onClick={recognize}>
          {t('calendar.text.recognize')}
        </Button>
      </div>
      <div className={styles.examples} role="group" aria-label={t('calendar.text.examplesLabel')}>
        {(['example1', 'example2', 'example3'] as const).map((key) => (
          <button
            key={key}
            type="button"
            className={styles.example}
            onClick={() => {
              setText(t(`calendar.text.${key}`));
            }}
          >
            {t(`calendar.text.${key}`)}
          </button>
        ))}
      </div>
      {parsed && (
        <div
          className={styles.result}
          role="status"
          data-testid="text-result"
          data-complete={parsed.complete}
          data-kind={parsed.kind}
        >
          <p className={styles.verdict}>
            {parsed.complete ? t('calendar.text.resultOk') : t('calendar.text.resultPartial')}
          </p>
          <Interpretation parsed={parsed} />
          {parsed.notes.length > 0 && (
            <ul className={styles.notes}>
              {parsed.notes.map((note, index) => (
                <li key={`${note.code}-${String(index)}`} data-kind={note.kind}>
                  <strong>{t(`calendar.text.kinds.${note.kind}`)}:</strong>{' '}
                  {t(`calendar.text.notes.${note.code as 'tooLong'}`, note.params)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );

  return (
    <CreateEventDialog
      {...rest}
      boards={boards}
      timezone={timezone}
      draft={draftFromDays(todayIn(timezone), null)}
      heading={t('calendar.text.title')}
      intro={intro}
      prefill={prefill}
    />
  );
}
